import { fetchMatches, fetchDraws, fetchDale } from '@/services/matchesService';

export default {
  namespaced: true,
  
  state: {
    games: [],
    filteredGames: [],
    totalGames: 0,
    currentPage: 1,
    itemsPerPage: 30,
    totalPages: 0,
    
    // Filters
    filters: {
      gameType: 'matches',
      searchTerm: '',
      dateFrom: '',
      dateTo: '',
      status: '',
      oddsMin: null,
      oddsMax: null,
      showDrawsOnly: false,
      showHighScoring: false,
    },
    
    // UI State
    viewMode: 'list', // 'list' or 'grid'
    sortBy: 'date',
    sortOrder: 'desc',
    
    // Loading states
    isLoading: false,
    isLoadingMore: false,
    error: null,
    
    // Cache
    lastFetch: null,
    cacheTimeout: 5 * 60 * 1000, // 5 minutes
  },
  
  mutations: {
    SET_GAMES(state, games) {
      state.games = games;
      state.lastFetch = Date.now();
    },
    
    APPEND_GAMES(state, games) {
      state.games = [...state.games, ...games];
    },
    
    SET_FILTERED_GAMES(state, games) {
      state.filteredGames = games;
    },
    
    SET_TOTAL_GAMES(state, total) {
      state.totalGames = total;
      state.totalPages = Math.ceil(total / state.itemsPerPage);
    },
    
    SET_CURRENT_PAGE(state, page) {
      state.currentPage = page;
    },
    
    SET_ITEMS_PER_PAGE(state, count) {
      state.itemsPerPage = count;
      state.totalPages = Math.ceil(state.totalGames / count);
      state.currentPage = 1;
    },
    
    SET_FILTERS(state, filters) {
      state.filters = { ...state.filters, ...filters };
    },
    
    RESET_FILTERS(state) {
      state.filters = {
        gameType: state.filters.gameType, // Keep game type
        searchTerm: '',
        dateFrom: '',
        dateTo: '',
        status: '',
        oddsMin: null,
        oddsMax: null,
        showDrawsOnly: false,
        showHighScoring: false,
      };
    },
    
    SET_VIEW_MODE(state, mode) {
      state.viewMode = mode;
    },
    
    SET_SORT(state, { sortBy, sortOrder }) {
      state.sortBy = sortBy;
      state.sortOrder = sortOrder;
    },
    
    SET_LOADING(state, isLoading) {
      state.isLoading = isLoading;
    },
    
    SET_LOADING_MORE(state, isLoading) {
      state.isLoadingMore = isLoading;
    },
    
    SET_ERROR(state, error) {
      state.error = error;
    },
    
    UPDATE_GAME(state, updatedGame) {
      const index = state.games.findIndex(game => game.id === updatedGame.id);
      if (index !== -1) {
        state.games.splice(index, 1, updatedGame);
      }
    },
  },
  
  actions: {
    async fetchGames({ commit, state }, { page = 1, append = false } = {}) {
      // Check cache
      if (!append && state.lastFetch && (Date.now() - state.lastFetch) < state.cacheTimeout) {
        console.log('Using cached games data');
        return;
      }
      
      commit(append ? 'SET_LOADING_MORE' : 'SET_LOADING', true);
      commit('SET_ERROR', null);
      
      try {
        let response;
        const { gameType, searchTerm } = state.filters;
        
        // Fetch based on game type
        switch (gameType) {
          case 'matches':
            response = await fetchMatches(page, searchTerm);
            break;
          case 'draws':
            response = await fetchDraws(page, searchTerm);
            break;
          default:
            response = await fetchDale(gameType, page, searchTerm);
        }
        
        if (response?.results) {
          if (append) {
            commit('APPEND_GAMES', response.results);
          } else {
            commit('SET_GAMES', response.results);
          }
          commit('SET_TOTAL_GAMES', response.count || response.results.length);
          commit('SET_CURRENT_PAGE', page);
        } else {
          throw new Error('Invalid response format');
        }
      } catch (error) {
        console.error('Error fetching games:', error);
        commit('SET_ERROR', error.message);
        if (!append) {
          commit('SET_GAMES', []);
        }
      } finally {
        commit(append ? 'SET_LOADING_MORE' : 'SET_LOADING', false);
      }
    },
    
    async loadMoreGames({ dispatch, state }) {
      if (state.currentPage < state.totalPages && !state.isLoadingMore) {
        await dispatch('fetchGames', { 
          page: state.currentPage + 1, 
          append: true 
        });
      }
    },
    
    applyFilters({ commit, state }) {
      // Apply client-side filtering
      let filtered = [...state.games];
      const filters = state.filters;
      
      // Search filter
      if (filters.searchTerm) {
        const searchTerms = filters.searchTerm.toLowerCase().split(' ');
        filtered = filtered.filter(game =>
          searchTerms.every(term =>
            game.times.toLowerCase().includes(term) ||
            game.placar.replace(':', 'x').includes(term)
          )
        );
      }
      
      // Date range filter
      if (filters.dateFrom) {
        filtered = filtered.filter(game => 
          new Date(game.date) >= new Date(filters.dateFrom)
        );
      }
      if (filters.dateTo) {
        filtered = filtered.filter(game => 
          new Date(game.date) <= new Date(filters.dateTo)
        );
      }
      
      // Status filter
      if (filters.status) {
        filtered = filtered.filter(game => game.status === filters.status);
      }
      
      // Odds range filter
      if (filters.oddsMin !== null) {
        filtered = filtered.filter(game => 
          game.odds && game.odds >= filters.oddsMin
        );
      }
      if (filters.oddsMax !== null) {
        filtered = filtered.filter(game => 
          game.odds && game.odds <= filters.oddsMax
        );
      }
      
      // Draw filter
      if (filters.showDrawsOnly) {
        filtered = filtered.filter(game => {
          const [home, away] = game.placar.split(':').map(Number);
          return home === away;
        });
      }
      
      // High scoring filter
      if (filters.showHighScoring) {
        filtered = filtered.filter(game => {
          const [home, away] = game.placar.split(':').map(Number);
          return (home + away) >= 3;
        });
      }
      
      // Apply sorting
      if (state.sortBy) {
        filtered.sort((a, b) => {
          let aVal = a[state.sortBy];
          let bVal = b[state.sortBy];
          
          if (state.sortBy === 'date') {
            aVal = new Date(aVal).getTime();
            bVal = new Date(bVal).getTime();
          }
          
          if (state.sortOrder === 'asc') {
            return aVal > bVal ? 1 : -1;
          } else {
            return aVal < bVal ? 1 : -1;
          }
        });
      }
      
      commit('SET_FILTERED_GAMES', filtered);
    },
    
    updateFilters({ commit, dispatch }, filters) {
      commit('SET_FILTERS', filters);
      commit('SET_CURRENT_PAGE', 1);
      
      // If game type changed, fetch new data
      if (filters.gameType !== undefined) {
        dispatch('fetchGames');
      } else {
        // Otherwise, just apply filters to existing data
        dispatch('applyFilters');
      }
    },
    
    resetFilters({ commit, dispatch }) {
      commit('RESET_FILTERS');
      dispatch('applyFilters');
    },
    
    setViewMode({ commit }, mode) {
      commit('SET_VIEW_MODE', mode);
    },
    
    setSorting({ commit, dispatch }, { sortBy, sortOrder }) {
      commit('SET_SORT', { sortBy, sortOrder });
      dispatch('applyFilters');
    },
    
    changePage({ commit, dispatch, state }, page) {
      commit('SET_CURRENT_PAGE', page);
      // If we need more data, fetch it
      if (page > Math.ceil(state.games.length / state.itemsPerPage)) {
        dispatch('fetchGames', { page });
      }
    },
    
    changeItemsPerPage({ commit }, count) {
      commit('SET_ITEMS_PER_PAGE', count);
    },
    
    refreshGames({ dispatch, state }) {
      // Force refresh by clearing cache
      state.lastFetch = null;
      return dispatch('fetchGames', { page: state.currentPage });
    },
  },
  
  getters: {
    allGames: state => state.games,
    
    displayedGames: state => {
      const games = state.filteredGames.length ? state.filteredGames : state.games;
      const start = (state.currentPage - 1) * state.itemsPerPage;
      const end = start + state.itemsPerPage;
      return games.slice(start, end);
    },
    
    totalFilteredGames: state => state.filteredGames.length || state.totalGames,
    
    hasActiveFilters: state => {
      const { filters } = state;
      return filters.searchTerm ||
        filters.dateFrom ||
        filters.dateTo ||
        filters.status ||
        filters.oddsMin !== null ||
        filters.oddsMax !== null ||
        filters.showDrawsOnly ||
        filters.showHighScoring;
    },
    
    activeFilterCount: (state, getters) => {
      if (!getters.hasActiveFilters) return 0;
      
      let count = 0;
      const { filters } = state;
      
      if (filters.searchTerm) count++;
      if (filters.dateFrom || filters.dateTo) count++;
      if (filters.status) count++;
      if (filters.oddsMin !== null || filters.oddsMax !== null) count++;
      if (filters.showDrawsOnly) count++;
      if (filters.showHighScoring) count++;
      
      return count;
    },
    
    upcomingGames: state => {
      const now = new Date();
      return state.games
        .filter(game => new Date(game.date) > now && game.status !== 'finished')
        .sort((a, b) => new Date(a.date) - new Date(b.date))
        .slice(0, 5);
    },
    
    liveGames: state => {
      return state.games.filter(game => game.status === 'live');
    },
    
    recentResults: state => {
      return state.games
        .filter(game => game.status === 'finished')
        .sort((a, b) => new Date(b.date) - new Date(a.date))
        .slice(0, 10);
    },
  },
};